import React, { useState, useEffect, useCallback } from 'react';
import { FiStar, FiTrendingUp, FiZap, FiSearch, FiRefreshCw, FiGrid } from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../../services/api';

const TABS = [
  { key: 'featured', label: 'Featured Picks', flag: 'isFeatured', color: '#7C3AED', icon: FiStar, desc: 'Products shown in the "Featured Picks" section on homepage' },
  { key: 'trending', label: 'Trending Now', flag: 'isTrending', color: '#F59E0B', icon: FiTrendingUp, desc: 'Products shown in the "Trending Now" section on homepage' },
  { key: 'new', label: 'New Arrivals', flag: 'isNew', color: '#10B981', icon: FiZap, desc: 'Products shown in the "New Arrivals" section on homepage' },
];

export default function AdminHomepage() {
  const [activeTab, setActiveTab] = useState('featured');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [togglingId, setTogglingId] = useState(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/admin/products?page=${page}&size=20`);
      const data = res.data;
      const items = data.content || (Array.isArray(data) ? data : []);
      setProducts(items);
      setTotal(data.totalElements || items.length);
      setTotalPages(data.totalPages || 1);
    } catch {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const currentTab = TABS.find(t => t.key === activeTab);

  const filtered = products.filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.brand.toLowerCase().includes(search.toLowerCase())
  );

  const toggleFlag = async (product) => {
    const flag = currentTab.flag;
    const newVal = !product[flag];
    setTogglingId(product.id);
    try {
      await api.put(`/api/admin/products/${product.id}/flags`, { [flag]: newVal });
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, [flag]: newVal } : p));
      toast.success(`${product.name} ${newVal ? 'added to' : 'removed from'} ${currentTab.label}`);
    } catch {
      toast.error('Failed to update — please try again');
    } finally {
      setTogglingId(null);
    }
  };

  const activeCount = products.filter(p => p[currentTab?.flag]).length;

  return (
    <div>
      {/* Header */}
      <div className="admin-card" style={{ marginBottom: 20 }}>
        <div style={{ padding: '20px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
            <FiGrid size={20} color="#7C3AED" />
            <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>Homepage Content Manager</h2>
          </div>
          <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: 0 }}>
            Control which products appear in each section of the main website homepage. Changes take effect immediately.
          </p>
        </div>
      </div>

      {/* Tab Selector */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        {TABS.map(tab => {
          const Icon = tab.icon;
          const cnt = products.filter(p => p[tab.flag]).length;
          const isActive = activeTab === tab.key;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
              padding: '16px 20px', borderRadius: 12, border: `2px solid ${isActive ? tab.color : 'var(--border)'}`,
              background: isActive ? `${tab.color}10` : 'var(--bg-card)', cursor: 'pointer',
              textAlign: 'left', transition: 'all 0.2s',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <Icon size={20} color={isActive ? tab.color : 'var(--text-muted)'} />
                <span style={{ fontSize: 22, fontWeight: 800, color: isActive ? tab.color : 'var(--text-muted)' }}>{cnt}</span>
              </div>
              <p style={{ fontSize: 14, fontWeight: 700, color: isActive ? tab.color : 'var(--text-secondary)', margin: 0 }}>{tab.label}</p>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tab.desc}</p>
            </button>
          );
        })}
      </div>

      {/* Product List */}
      <div className="admin-card">
        <div className="admin-card-header">
          <div>
            <h3 style={{ margin: 0 }}>{currentTab?.label}</h3>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '2px 0 0' }}>
              {activeCount} product{activeCount !== 1 ? 's' : ''} active · click toggle to add/remove from homepage
            </p>
          </div>
          <div className="admin-card-actions">
            <div className="admin-search">
              <FiSearch />
              <input placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <button onClick={fetchProducts} style={{ padding: '8px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
              <FiRefreshCw size={15} />
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
            <div style={{ width: 32, height: 32, border: '3px solid #E5E7EB', borderTopColor: '#7C3AED', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
            Loading products...
          </div>
        ) : (
          <>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Product</th><th>Category</th><th>Price</th><th>Stock</th>
                    <th style={{ color: currentTab?.color }}>{currentTab?.label}</th>
                    <th>Other Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No products found</td></tr>
                  ) : filtered.map(p => {
                    const isActive = !!p[currentTab.flag];
                    const disc = p.originalPrice && p.price ? Math.round((1 - p.price / p.originalPrice) * 100) : 0;
                    return (
                      <tr key={p.id} style={{ background: isActive ? `${currentTab.color}06` : 'transparent' }}>
                        <td>
                          <div className="product-table-info">
                            <div className="product-table-image">
                              {p.imageUrl ? <img src={p.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (p.imageEmoji || '👗')}
                            </div>
                            <div>
                              <span className="product-table-name">{p.name}</span>
                              <span className="product-table-brand">{p.brand}</span>
                            </div>
                          </div>
                        </td>
                        <td><span className="table-badge badge-blue">{p.category}</span></td>
                        <td>
                          <span style={{ fontWeight: 700 }}>₹{Number(p.price).toLocaleString()}</span>
                          {disc > 0 && <span style={{ fontSize: 11, color: '#10B981', marginLeft: 6 }}>{disc}% off</span>}
                        </td>
                        <td style={{ color: p.stock === 0 ? '#EF4444' : 'var(--text-secondary)', fontSize: 13 }}>
                          {p.stock === 0 ? '⚠ Out of stock' : p.stock}
                        </td>
                        <td>
                          <button
                            onClick={() => toggleFlag(p)}
                            disabled={togglingId === p.id}
                            style={{
                              padding: '6px 16px', borderRadius: 20, border: `1.5px solid ${isActive ? currentTab.color : 'var(--border)'}`,
                              background: isActive ? currentTab.color : 'var(--bg-card)',
                              color: isActive ? 'white' : 'var(--text-secondary)',
                              fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s',
                              opacity: togglingId === p.id ? 0.6 : 1,
                            }}
                          >
                            {togglingId === p.id ? '...' : isActive ? `✓ In ${currentTab.label}` : `Add to ${currentTab.label}`}
                          </button>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {p.isFeatured && currentTab.flag !== 'isFeatured' && <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, background: '#7C3AED18', color: '#7C3AED', fontWeight: 700 }}>FEAT</span>}
                            {p.isTrending && currentTab.flag !== 'isTrending' && <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, background: '#F59E0B18', color: '#F59E0B', fontWeight: 700 }}>TREND</span>}
                            {p.isNew && currentTab.flag !== 'isNew' && <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, background: '#10B98118', color: '#10B981', fontWeight: 700 }}>NEW</span>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="admin-pagination">
                <p>Page {page + 1} of {totalPages} · {total} total products</p>
                <div className="admin-pagination-btns">
                  <button className="page-btn" disabled={page === 0} onClick={() => setPage(p => p - 1)}>‹</button>
                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                    const pg = totalPages <= 7 ? i : Math.min(Math.max(page - 3, 0), totalPages - 7) + i;
                    return <button key={pg} className={`page-btn${pg === page ? ' active' : ''}`} onClick={() => setPage(pg)}>{pg + 1}</button>;
                  })}
                  <button className="page-btn" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>›</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
